import { useState } from "react";
import { ShoppingCart, X } from "lucide-react";
import type { OperatorEntryRequest } from "../../api/client";
import { OperatorEntryError, type OperatorEntryState } from "../../hooks/useOperatorEntry";
import { cn } from "@/lib/utils";

/**
 * « Acheter maintenant » (2026-10-22) — entrée opérateur FORCÉE dans le cycle AUTO, deux clics
 * avec récapitulatif serveur (taille, stop, pire-cas après, jambes), sans OTP. Variante
 * {@code cycle} (cockpit : jambe OPERATEUR au tick suivant, un seul essai) et {@code auto}
 * (carte Autopilote hors cycle : lève la pause puis ouvre un cycle BUY). Rend rien si le mode
 * n'est pas disponible — le composant parent décide de le monter.
 */
export default function OperatorBuyControl({
  entry,
  variant,
}: {
  entry: OperatorEntryState;
  variant: "cycle" | "auto";
}) {
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const st = entry.status;
  const pv = st?.preview ?? null;
  const req = st?.request ?? null;
  const expectedMode = variant === "cycle" ? "IN_CYCLE" : "NEW_CYCLE";
  if (!st || st.mode !== expectedMode) return null;

  const inFlight = req?.status === "PENDING" || req?.status === "CONSUMED";

  const confirm = async () => {
    setErr(null);
    setDone(null);
    try {
      const r = await entry.request();
      if ("cycleUid" in r) setDone(`Cycle BUY ouvert (${String(r.cycleUid).slice(0, 8)}…).`);
    } catch (e) {
      if (e instanceof OperatorEntryError) {
        if (e.code === "CYCLE_START_REFUSED" && e.reason.includes("ACTIVE_CYCLE_EXISTS")) {
          setDone("Un cycle vient de s'ouvrir.");
        } else {
          setErr(humanRefusal(e.code, e.reason));
        }
      } else {
        setErr("Demande impossible — réessaie.");
      }
    } finally {
      setConfirming(false);
    }
  };

  const pct =
    pv && pv.riskBudgetEur > 0 ? Math.round((pv.worstCaseAfterEur / pv.riskBudgetEur) * 100) : null;

  return (
    <div className="flex flex-col gap-1.5" data-testid="operator-buy">
      {confirming && pv ? (
        <div className="border-warning/45 bg-warning/5 flex flex-col gap-2 rounded-lg border p-2.5 text-xs">
          <div className="font-medium">
            {variant === "cycle"
              ? "Jambe forcée au tick suivant — mêmes gates qu'un renfort, un seul essai."
              : `Lève la pause AUTO et ouvre un cycle BUY maintenant.`}
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono tabular-nums" data-testid="operator-buy-recap">
            <dt className="text-muted-foreground">Sens · taille</dt>
            <dd>
              {pv.direction} · {Number(pv.sizePerPointEur).toFixed(1)} €/pt
            </dd>
            <dt className="text-muted-foreground">Stop garanti</dt>
            <dd>{Number(pv.stopDistancePoints).toFixed(0)} pts</dd>
            <dt className="text-muted-foreground">Pire-cas après</dt>
            <dd className={cn(pct != null && pct > 90 && "text-loss")}>
              {Number(pv.worstCaseAfterEur).toFixed(0)} € / {Number(pv.riskBudgetEur).toFixed(0)} €
              {pct != null ? ` (${pct} %)` : ""}
            </dd>
            <dt className="text-muted-foreground">Jambes</dt>
            <dd>
              {pv.slotsUsed} → {pv.slotsUsed + 1} / {pv.maxSide}
            </dd>
          </dl>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={entry.pending}
              className="bg-warning flex-1 rounded-lg px-3 py-1.5 text-xs font-bold text-black disabled:opacity-50"
              data-testid="operator-buy-confirm"
            >
              {entry.pending ? "Envoi…" : "Confirmer l'achat"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="border-border text-muted-foreground hover:text-foreground rounded-lg border px-3 py-1.5 text-xs"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setErr(null);
            setDone(null);
            setConfirming(true);
          }}
          disabled={entry.pending || inFlight || !pv}
          className="text-warning border-warning/45 hover:bg-warning/10 inline-flex w-fit items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
          data-testid="operator-buy-begin"
          title={
            variant === "cycle"
              ? "Ajoute une jambe maintenant, sans attendre le rebond de swing (mêmes gates §7)."
              : "Lève la pause AUTO et ouvre un cycle BUY maintenant."
          }
        >
          <ShoppingCart className="size-3.5" aria-hidden /> Acheter maintenant
        </button>
      )}

      {req && variant === "cycle" && <RequestOutcome req={req} onCancel={() => void entry.cancel()} />}
      {done && (
        <p className="text-gain text-xs" data-testid="operator-buy-result">
          {done}
        </p>
      )}
      {err && (
        <p className="text-loss text-xs" role="alert" data-testid="operator-buy-error">
          {err}
        </p>
      )}
    </div>
  );
}

function RequestOutcome({ req, onCancel }: { req: OperatorEntryRequest; onCancel: () => void }) {
  switch (req.status) {
    case "PENDING":
      return (
        <p className="text-muted-foreground flex items-center gap-2 text-xs" data-testid="operator-buy-result">
          Demande en attente du prochain tick…
          <button type="button" onClick={onCancel} className="hover:text-foreground inline-flex items-center gap-1" data-testid="operator-buy-cancel">
            <X className="size-3" aria-hidden /> Annuler
          </button>
        </p>
      );
    case "CONSUMED":
      return (
        <p className="text-muted-foreground text-xs" data-testid="operator-buy-result">
          Ordre en cours chez IG…
        </p>
      );
    case "FILLED":
      return (
        <p className="text-gain text-xs" data-testid="operator-buy-result">
          Achat exécuté{req.dealId ? ` (deal ${req.dealId})` : ""}.
        </p>
      );
    case "REFUSED":
    case "EXPIRED":
      return (
        <p className="text-loss text-xs" role="alert" data-testid="operator-buy-result">
          Refusé — {humanRefusal(req.status, req.reason ?? "")}
        </p>
      );
    default:
      return null;
  }
}

/** Motif humain à partir du code 409 ou du motif de refus au tick (préfixe SKIP_* / breaker / gateway). */
export function humanRefusal(code: string, reason: string): string {
  const r = `${code} ${reason}`;
  if (r.includes("AUTO_NOT_ARMED")) return "AUTO n'est pas armé.";
  if (r.includes("REQUEST_PENDING")) return "Une demande est déjà en cours.";
  if (r.includes("INITIALE_PENDING")) return "La première jambe n'est pas encore ouverte.";
  if (r.includes("CYCLE_FLAT")) return "Le cycle est à plat et va se terminer.";
  if (r.includes("STREAM_NOT_STARTED")) return "Flux de prix non démarré (marché fermé ?).";
  if (r.includes("NO_OPEN_WINDOW")) return "Moins de 60 min avant la clôture : pas de nouveau cycle.";
  if (r.includes("MARKET_CLOSING_SOON")) return "Clôture imminente (fenêtre de flatten).";
  if (r.includes("QUOTE_PREFLIGHT_BLOCKED") || r.includes("SKIP_REINFORCE_SPREAD") || r.includes("STREAM_HEALTH"))
    return "Spread ou flux hors limites à cet instant.";
  if (r.includes("SKIP_REINFORCE_TOTAL_LIMIT") || r.includes("PYRAMIDING")) return "Plafond de jambes atteint.";
  if (r.includes("SKIP_BUDGET") || r.includes("WORST_CASE")) return "Budget §7 pire-cas dépassé.";
  if (r.includes("SKIP_INSUFFICIENT_MARGIN")) return "Marge insuffisante.";
  if (r.includes("OPEN_REJECTED")) return `Ordre rejeté par IG (${reason.replace(/^OPEN_REJECTED:\s*/, "")}).`;
  if (r.includes("EXPIRED")) return "Aucun tick dans les 10 s — demande expirée.";
  if (r.includes("AUTO_DISARMED")) return "AUTO a été désarmé entre-temps.";
  if (r.includes("CYCLE_CHANGED")) return "Le cycle a changé entre-temps.";
  return reason || code;
}
